import { useQuery } from 'react-query';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { notifyError } from '@/portainer/services/notifications';
import axios, { parseAxiosError } from '@/portainer/services/axios';

export type K8sNodeLimits = {
  CPU: number;
  Memory: number;
};

type NodeLimitsQueryParams = {
  overCommit?: boolean;
  overCommitPercent?: number;
};

/**
 * useClusterResourceLimitsQuery is used to retrieve the total resource limits for a cluster, minus the allocated resources taken by existing namespaces
 * @returns the available resource limits for the cluster
 * */
export function useClusterResourceLimitsQuery(
  environmentId: EnvironmentId,
  params?: NodeLimitsQueryParams
) {
  return useQuery(
    [
      'environments',
      environmentId,
      'kubernetes',
      'max_resource_limits',
      params,
    ],
    () => getResourceLimits(environmentId, params),
    {
      onError: (err) => {
        notifyError('Failure', err as Error, 'Unable to get resource limits');
      },
    }
  );
}

async function getResourceLimits(
  environmentId: EnvironmentId,
  params?: NodeLimitsQueryParams
) {
  try {
    const { data: limits } = await axios.get<K8sNodeLimits>(
      `/kubernetes/${environmentId}/max_resource_limits`,
      {
        params,
      }
    );
    return limits;
  } catch (e) {
    throw parseAxiosError(e, 'Unable to retrieve resource limits');
  }
}
